/**
 * THE CLIENT'S RECORDED INJURIES, AS THE COACH READS THEM — the Overview's `Limitations/Injuries`
 * card (`EVERFIT-MAP` §2-bis column 2; Fable's ruling 2026-09-24, 3(b)).
 *
 * 🔴 **THE CARD THIS REPLACES WAS FALSE FOR EVERY CLIENT.** The setup column printed `Injuries:
 * Recorded — the engine reads and filters on them` whenever `profiles.injuries_data` was not
 * NULL. MEASURED live 2026-09-24: all 7 profiles store `[]` — an empty array, never NULL — so the
 * sentence claimed recorded injuries for people who had told us of none. An empty collection is
 * one value for two answers (the codex, silence-is-not-success); this module reads the ENTRIES.
 *
 * ⚠️ **A HAND PORT OF `HybridTrainer/Models/Injury.swift`, AND IT SAYS WHICH PARTS.** The phone
 * decodes tolerantly — `bodyPart` required, every other field defaulted — because a safety input
 * may not vanish when it grows a field; this reader does the same, and an entry with no body part
 * is SKIPPED rather than guessed at (the Swift decode throws for it too). `fullLabel` and the
 * TWO-word severity (`Manageable` for stored 1 and 2, `Severe` for 3 — H6, 2026-08-15) are the
 * phone's own words, so a coach and an athlete never read two names for one stored value.
 *
 * ⚠️ READ-ONLY. A coach-written limitations field would be a new column and a policy — his, and
 * filed under the Goal & Countdown ledger row, not built here.
 */

export type InjuryLine = {
  key: string;
  /** `Left Knee (Front)` — `Injury.fullLabel`, verbatim in shape. */
  label: string;
  /** `Manageable` / `Severe` — `Injury.severityLabel`. */
  severity: "Manageable" | "Severe";
  /** Symptoms, confirmed conditions and the athlete's own words, in that order; empty ones dropped. */
  details: string[];
};

const strings = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === "string" && x.trim() !== "") : [];

/** `Injury.severityLabel`: stored 3 and above is Severe; 1 (legacy Mild) and 2 are Manageable. */
export function severityLabel(severity: unknown): "Manageable" | "Severe" {
  // The Swift decode defaults a missing severity to 2, which reads Manageable — the same here.
  return typeof severity === "number" && severity >= 3 ? "Severe" : "Manageable";
}

/** `Injury.fullLabel`: `[side] bodyPart [(Front/Rear)]`. */
export function injuryLabel(bodyPart: string, side: unknown, aspects: unknown): string {
  const parts = [bodyPart];
  if (typeof side === "string" && side.trim() !== "") parts.unshift(side);
  const a = strings(aspects);
  if (a.length > 0) parts.push(`(${a.join("/")})`);
  return parts.join(" ");
}

/**
 * Every recorded injury, in the order the athlete entered them. `[]` for NULL, for `[]`, and for
 * anything that is not an array — the three are one answer to a coach: nothing recorded.
 */
export function readInjuries(raw: unknown): InjuryLine[] {
  if (!Array.isArray(raw)) return [];
  const out: InjuryLine[] = [];
  raw.forEach((entry, index) => {
    if (entry === null || typeof entry !== "object") return;
    const e = entry as Record<string, unknown>;
    const bodyPart = typeof e.bodyPart === "string" ? e.bodyPart.trim() : "";
    if (bodyPart === "") return;
    const custom = typeof e.customDescription === "string" ? e.customDescription.trim() : "";
    out.push({
      key: typeof e.id === "string" && e.id !== "" ? e.id : `injury-${String(index)}`,
      label: injuryLabel(bodyPart, e.side, e.aspects),
      severity: severityLabel(e.severity),
      details: [
        ...strings(e.symptoms),
        ...strings(e.specificConditions),
        ...(custom === "" ? [] : [custom]),
      ],
    });
  });
  return out;
}
